import { Receipt, Link as LinkIcon } from "@phosphor-icons/react";
import ContentLoader from "../shared/ContentLoader";
import {
  normalizeTransactionStatus,
  getTransactionStatusLabel,
  TRANSACTION_STATUS,
} from "../../firebase";
import { PAYMENT_METHOD_LABELS } from "../../constants/paymentMethods";
// Grow payment links (Phase 5): chip / request button per row — read-only, no inline mutations added
import useGrowLinksMap from "../grow/useGrowLinksMap";
import GrowLinkStatusChip from "../grow/GrowLinkStatusChip";
import RequestPaymentButton from "../grow/RequestPaymentButton";
import "./FinanceTransactionsList.css";

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "—";
  return `₪${amount.toLocaleString("he-IL")}`;
};

/** Map a raw status to the badge bucket used for colors + short label. */
export const getStatusBucket = (status) => {
  const normalized = normalizeTransactionStatus(status);
  if (
    normalized === TRANSACTION_STATUS.INVOICE_MANUAL ||
    normalized === TRANSACTION_STATUS.INVOICE_AUTO
  ) {
    return "invoiced";
  }
  return normalized; // expected | pending | paid | split | refunded | cancelled
};

const SHORT_STATUS_LABELS = {
  expected: "עתידי",
  pending: "ממתין",
  paid: "שולם",
  invoiced: "חשבונית",
  split: "פוצל",
  refunded: "זיכוי",
  cancelled: "בוטל",
};

const txDate = (tx) =>
  tx.actualPaymentDate || tx.expectedPaymentDate || tx.createdAt;

/**
 * Finance list view — flat month list of transactions across all statuses.
 * Row click opens PaymentDetailModal (no inline mutations — all status
 * changes go through the existing, tested flows).
 */
function FinanceTransactionsList({ transactions, isLoading, onRowClick }) {
  const growLinks = useGrowLinksMap(transactions);

  if (isLoading) {
    return (
      <ContentLoader fill message="טוען תנועות..." />
    );
  }

  if (!transactions.length) {
    return (
      <div className="fin-list-empty">
        <Receipt size={36} weight="light" />
        <p>אין תנועות בחודש זה</p>
        <span>נסו לשנות חודש או לנקות סינונים</span>
      </div>
    );
  }

  return (
    <div className="fin-list">
      {transactions.map((tx) => {
        const bucket = getStatusBucket(tx.status);
        const isManual = !tx.appointmentId;
        const hasInvoice =
          tx.RecepitsDocsRefArray && tx.RecepitsDocsRefArray.length > 0;
        const invoiceData = hasInvoice ? tx.RecepitsDocsRefArray[0] : null;
        const dateValue = txDate(tx);
        const dateLabel = dateValue
          ? new Date(dateValue).toLocaleDateString("he-IL", {
              day: "numeric",
              month: "short",
            })
          : "—";

        return (
          <div
            key={tx.id}
            className={`fin-row fin-row-${bucket}`}
            onClick={() => onRowClick(tx)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) return; // inner buttons (invoice link, Grow chip/button) handle their own keys
              if (e.key === "Enter") onRowClick(tx);
            }}
          >
            <span className={`fin-row-dot fin-dot-${bucket}`} />

            <div className="fin-row-info">
              <span className="fin-row-name">
                {tx.patientName || tx.description || "הכנסה"}
              </span>
              <div className="fin-row-meta">
                <span>{dateLabel}</span>
                {tx.paymentMethod && (
                  <>
                    <span className="fin-meta-dot" />
                    <span>{PAYMENT_METHOD_LABELS[tx.paymentMethod] || "אחר"}</span>
                  </>
                )}
                {isManual && (
                  <>
                    <span className="fin-meta-dot" />
                    <span>ידני</span>
                  </>
                )}
                {tx.manualInvoiceNumber && (
                  <>
                    <span className="fin-meta-dot" />
                    <span>חשבונית {tx.manualInvoiceNumber}</span>
                  </>
                )}
              </div>
            </div>

            {hasInvoice && (
              <a
                href={invoiceData.invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="fin-row-invoice-link"
                title={`חשבונית ${invoiceData.invoiceNumber || ""}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Receipt size={15} weight="fill" />
                <LinkIcon size={11} weight="bold" />
              </a>
            )}

            {(() => {
              const growLink = growLinks.linkFor(tx);
              if (growLink) {
                return (
                  <GrowLinkStatusChip link={growLink} size="xs" onClick={() => growLinks.openLink(growLink)} />
                );
              }
              if (bucket === "expected" || bucket === "pending") {
                return <RequestPaymentButton transactions={[tx]} size="sm" variant="ghost" />;
              }
              return null;
            })()}

            <span
              className={`fin-row-status fin-status-${bucket}`}
              title={getTransactionStatusLabel(tx.status)}
            >
              {SHORT_STATUS_LABELS[bucket] || bucket}
            </span>

            <span className={`fin-row-amount fin-amount-${bucket}`}>
              {formatCurrency(tx.amount)}
            </span>
          </div>
        );
      })}
      {growLinks.popover}
    </div>
  );
}

export default FinanceTransactionsList;
